import { useMutation } from '@apollo/client';
import type { SlimBannerProps } from '@stitch-fix/mode-react';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useClientAuth } from '@stitch-fix/sf-next';
import type { ModeBannerSlimVariant } from '../../types/definitions/vendor/graphql-schema.d';
import { UtmPromoSessionCreateDocument, type UtmPromoSessionCreateMutation, type UtmPromoSessionCreateMutationVariables } from './UtmPromoSessionCreate';
export const UTM_BANNER_VARIANT_MAP: Record<ModeBannerSlimVariant, SlimBannerProps['variant']> = {
  BLUE_95: 'blue-95',
  BLUSH: 'blush',
  BUTTER: 'butter',
  CARGO: 'cargo',
  CERULEAN_WHITE: 'cerulean-white',
  CHARCOAL: 'charcoal',
  DEFAULT: 'promo-dark',
  PROMOTIONAL: 'promo-dark',
  CITRUS: 'citrus',
  DENIM: 'denim',
  FOREST: 'forest',
  GRAPEFRUIT: 'grapefruit',
  GRAY_99: 'gray-99',
  INVERSE: 'dark',
  KHAKI: 'khaki',
  MINT_95: 'mint-95',
  NAVY_CITRUS: 'navy-citrus',
  NAVY_WHITE: 'navy-white',
  SHORELINE: 'shoreline',
  URGENT: 'error-dark'
};
const inferBusinessLine = (pathname: string): string | undefined => {
  if (pathname.match(/\/women/)) return 'women';
  if (pathname.match(/\/men/)) return 'men';
  if (pathname.match(/\/kids/)) return 'kids';
  return undefined;
};

// utm_campaign is a pipe-separated string, e.g.:
// social|facebook|womens|w|fix|ret|dormant|web-app|us|conversion|octfy27-promo|GT1Y|promo-7E7ZZ26ZCX
// We extract the offer name from the segment prefixed with "promo-".
export const extractOfferName = (utmCampaign: string): string | null => {
  const segment = utmCampaign.split('|').find(s => s.startsWith('promo-'));
  if (!segment) return null;
  const candidate = segment.slice('promo-'.length, 'promo-'.length + 10);
  return /^[A-Za-z0-9]{10}$/.test(candidate) ? candidate : null;
};
export const useVisitorDiscountOffer = (skip = false) => {
  const {
    asPath
  } = useRouter();
  const {
    authStatus
  } = useClientAuth();
  const authStatusLoading = authStatus === 'loading';
  const searchString = asPath.includes('?') ? asPath.slice(asPath.indexOf('?')) : '';
  const utmCampaign = new URLSearchParams(searchString).get('utm_campaign');
  const offerName = utmCampaign ? extractOfferName(utmCampaign) : null;
  const pathname = asPath.split('?')[0];
  const businessLine = inferBusinessLine(pathname);
  const shouldSkip = skip || !offerName || authStatusLoading;
  const [execute, {
    data,
    loading: requestLoading,
    error,
    called: mutationCalled
  }] = useMutation<UtmPromoSessionCreateMutation, UtmPromoSessionCreateMutationVariables>(UtmPromoSessionCreateDocument);
  useEffect(() => {
    if (shouldSkip) return;
    execute({
      variables: {
        input: {
          offerName,
          ...(businessLine ? {
            businessLine
          } : {})
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-empty-function
    }).catch(() => {});
  }, [shouldSkip, offerName, businessLine, execute]);
  const result = data?.utmPromoSessionCreate;
  const success = result?.__typename === 'UtmPromoSessionSuccess' // eslint-disable-line no-underscore-dangle
  ? result : null;
  return {
    content: shouldSkip ? null : success?.content ?? null,
    offerName: shouldSkip ? null : offerName,
    loading: !skip && !!offerName && (requestLoading || !mutationCalled || authStatusLoading),
    error: shouldSkip ? undefined : error
  };
};